import React, { useMemo } from 'react';
import {
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Paprika, PaprikaRadius, PaprikaSpacing, PaprikaTypography } from '../../styles/theme';
import { PlannedMeal } from '../../redux/mealPlanSlice';
import { PersonalRecipeIndexEntry } from '../../services/personalRecipeIndex';
import { RecipeMetadata } from '../../redux/recipeMetadataSlice';
import {
  getTopSuggestions,
  ScoredSuggestion,
  isVegetarianRecipe,
  isFishRecipe,
  WeekSlot,
} from '../../services/weekGeneratorService';
import SuggestionCard from './SuggestionCard';
import { useSuggestionPool } from '../../hooks/useSuggestionPool';
import { useTranslation } from 'react-i18next';
import { formatWeekday } from '../../utils/dateLocale';

interface SwapSuggestionSheetProps {
  visible: boolean;
  meal: PlannedMeal | null;
  allRecipes: PersonalRecipeIndexEntry[];
  currentWeekRecipeIds: string[];
  metadata: Record<string, RecipeMetadata>;
  onConfirm: (meal: PlannedMeal, suggestion: PersonalRecipeIndexEntry) => void;
  onClose: () => void;
}

export const SwapSuggestionSheet: React.FC<SwapSuggestionSheetProps> = ({
  visible,
  meal,
  allRecipes,
  currentWeekRecipeIds,
  metadata,
  onConfirm,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  // Allergies, diets and the same-meal rules first: `getTopSuggestions` knows none of them.
  const pool = useSuggestionPool(allRecipes, meal?.scheduledFor ?? null, meal?.id, meal?.mealType);
  const filteredWeekIds = useMemo(
    () => currentWeekRecipeIds.filter(id => id !== meal?.recipeId),
    [currentWeekRecipeIds, meal?.recipeId],
  );

  const slot: WeekSlot | null = useMemo(() => {
    if (!meal) return null;
    const d = new Date(meal.scheduledFor + 'T12:00:00');
    const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
    return {
      date: meal.scheduledFor,
      dayOfWeek: meal.dayOfWeek,
      dayName,
      mealType: meal.mealType,
      isWeekend: meal.dayOfWeek === 0 || meal.dayOfWeek === 6,
    };
  }, [meal]);

  const { suggestions } = useMemo((): { suggestions: ScoredSuggestion[]; usedFallback: boolean } => {
    if (!visible || !meal || !slot || pool.length === 0) {
      return { suggestions: [], usedFallback: false };
    }
    const dietaryFilteredRecipes =
      meal.dietaryRole === 'vegetarian' ? pool.filter(isVegetarianRecipe)
      : meal.dietaryRole === 'fish' ? pool.filter(isFishRecipe)
      : pool;
    return getTopSuggestions(slot, dietaryFilteredRecipes, filteredWeekIds, [], metadata, undefined, 5);
  }, [visible, meal, slot, pool, filteredWeekIds, metadata]);

  const slotTitle = useMemo(() => {
    if (!meal) return '';
    return t('thisWeek.slotTitle', {
      day: formatWeekday(meal.scheduledFor, i18n.language),
      meal: t(`thisWeek.mealTypes.${meal.mealType}`),
    });
  }, [meal, t, i18n.language]);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity style={styles.sheet} activeOpacity={1} onPress={() => {}}>
          <View style={styles.dragIndicator} />

          <View style={styles.header}>
            <View>
              <Text style={styles.headerLabel}>{t('thisWeek.swapSuggestion')}</Text>
              <Text style={styles.headerTitle}>{slotTitle}</Text>
            </View>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="close" size={24} color={Paprika.textSecondary} />
            </TouchableOpacity>
          </View>

          {suggestions.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>{t('thisWeek.noAlternativesFound')}</Text>
            </View>
          ) : (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.suggestionsContainer}
            >
              {suggestions.map((s) => (
                <SuggestionCard
                  key={s.recipe.id}
                  recipe={s.recipe}
                  badges={s.badges}
                  onPress={() => { onConfirm(meal!, s.recipe); onClose(); }}
                  onBadgePress={() => {}}
                />
              ))}
            </ScrollView>
          )}
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: Paprika.background,
    borderTopLeftRadius: PaprikaRadius.xl,
    borderTopRightRadius: PaprikaRadius.xl,
    paddingBottom: Platform.OS === 'ios' ? 34 : 16,
  },
  dragIndicator: {
    width: 40,
    height: 4,
    backgroundColor: Paprika.borderLight,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 12,
    marginBottom: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: PaprikaSpacing.lg,
    paddingVertical: PaprikaSpacing.md,
  },
  headerLabel: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
  },
  headerTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginTop: 2,
  },
  closeButton: {
    padding: PaprikaSpacing.xs,
  },
  suggestionsContainer: {
    paddingHorizontal: PaprikaSpacing.lg,
    paddingVertical: PaprikaSpacing.sm,
  },
  emptyContainer: {
    height: 120,
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: PaprikaSpacing.md,
  },
  emptyText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
  },
});

export default SwapSuggestionSheet;
